import { useState } from "react";
import { toast } from "sonner";
import { FileText, Plus, Pencil, Trash2, Baby, User } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { useAuth } from "../../lib/auth";
import { useLang } from "../../lib/i18n";
import { useFetch } from "../../lib/useFetch";
import { api, errMsg, fileUrl, fmtDate } from "../../lib/api";
import { FileUpload } from "../../components/site/FileUpload";
import { SailorFields, SportFields, Field, isMinor } from "../../components/site/ProfileFields";
import { H } from "./AccountPages";
import { GuardianConsent } from "../../components/site/Consent";
import { AppPanel } from "./AppPanel";

const Card = ({ title, children, right }) => (
  <section className="rounded-3xl border border-slate-200 bg-white p-6 md:p-8">
    <div className="mb-6 flex flex-wrap items-center justify-between gap-3"><h2 className="font-display text-xl font-bold text-[#0A2540]">{title}</h2>{right}</div>{children}
  </section>
);

export const SailorDialog = ({ initial, onClose, onSaved }) => {
  const { t } = useLang();
  const [f, setF] = useState({ classes: [], boats: [], interest_classes: [], ...initial });
  const save = async () => {
    try {
      const { data } = f.id ? await api.put(`/me/participants/${f.id}`, f) : await api.post("/me/participants", f);
      toast.success(t("saved"));
      onSaved(data);
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[90vh] max-w-3xl overflow-y-auto rounded-3xl" data-testid="sailor-dialog">
        <DialogHeader><DialogTitle className="font-display text-2xl">{f.first_name ? `${f.first_name} ${f.last_name || ""}` : f.relation === "self" ? t("add_myself") : t("add_child")}</DialogTitle></DialogHeader>
        <div className="space-y-6">
          <div className="flex items-center gap-4">
            {f.photo_path && <img src={fileUrl(f.photo_path)} alt="" className="h-20 w-20 rounded-2xl object-cover" data-testid="sailor-photo" />}
            <Field label={t("photo")}><FileUpload accept="image/*" label={t("upload")} onUploaded={(x) => setF({ ...f, photo_path: x.path })} testid="sailor-photo-upload" /></Field>
          </div>
          <SailorFields f={f} setF={setF} />
          {isMinor(f.birth_date) && <p className="flex items-center gap-2 rounded-2xl bg-cyan-50 p-3 text-sm text-[#0A2540]"><Baby size={16} className="text-[#00A8B5]" />{t("minor_detected")}</p>}
          <SportFields f={f} setF={setF} />
          {!f.id && isMinor(f.birth_date) && <GuardianConsent checked={f.guardian_consent} onChange={(v) => setF({ ...f, guardian_consent: v })} />}
          <button onClick={save} className="btn-buoy" data-testid="sailor-save-button">{t("save")}</button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

const Sailors = () => {
  const { user, refresh } = useAuth();
  const { t } = useLang();
  const [edit, setEdit] = useState(null);
  const parts = user.participants || [];
  const hasSelf = parts.some((p) => p.relation === "self");
  const del = async (p) => {
    if (!window.confirm(`${t("remove")} ${p.first_name}?`)) return;
    try { await api.delete(`/me/participants/${p.id}`); refresh(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Card title={t("my_sailors")} right={
      <div className="flex gap-2">
        <button onClick={() => setEdit({ relation: "child", last_name: user.last_name, address: user.address, country: user.country })} className="inline-flex items-center gap-1 rounded-full bg-[#0A2540] px-4 py-2 text-sm font-semibold text-white" data-testid="add-child-button"><Plus size={14} />{t("add_child")}</button>
        {!hasSelf && !isMinor(user.birth_date) && <button onClick={() => setEdit({ relation: "self", ...Object.fromEntries(["first_name", "last_name", "birth_date", "birth_place", "tax_code", "address", "country", "phone"].map((k) => [k, user[k]])) })} className="inline-flex items-center gap-1 rounded-full border border-slate-300 px-4 py-2 text-sm font-semibold" data-testid="add-myself-button"><User size={14} />{t("add_myself")}</button>}
      </div>}>
      <div className="grid gap-4 md:grid-cols-2">
        {parts.map((p) => (
          <div key={p.id} className="flex items-center gap-4 rounded-2xl border border-slate-200 p-4" data-testid={`sailor-card-${p.id}`}>
            <div className="flex h-14 w-14 shrink-0 items-center justify-center overflow-hidden rounded-full bg-gradient-to-br from-[#0A2540] to-[#00A8B5] font-bold text-white">
              {p.photo_path ? <img src={fileUrl(p.photo_path)} alt="" className="h-full w-full object-cover" /> : `${p.first_name[0]}${p.last_name[0]}`}
            </div>
            <div className="min-w-0 flex-1">
              <p className="font-display font-bold text-[#0A2540]">{p.first_name} {p.last_name}</p>
              <p className="text-xs text-slate-500">{[p.relation === "self" ? t("myself") : t("child"), fmtDate(p.birth_date), (p.classes || []).join(", ")].filter(Boolean).join(" · ")}</p>
            </div>
            <button onClick={() => setEdit(p)} className="text-slate-400 hover:text-[#0A2540]" data-testid={`sailor-edit-${p.id}`}><Pencil size={16} /></button>
            <button onClick={() => del(p)} className="text-slate-400 hover:text-rose-500" data-testid={`sailor-delete-${p.id}`}><Trash2 size={16} /></button>
          </div>
        ))}
      </div>
      {edit && <SailorDialog initial={edit} onClose={() => setEdit(null)} onSaved={() => { setEdit(null); refresh(); }} />}
    </Card>
  );
};

export const profilePhoto = (u) => u?.photo_path || (u?.participants || []).find((p) => p.relation === "self")?.photo_path || "";

const PhotoCard = () => {
  const { user, setUser } = useAuth();
  const { t } = useLang();
  const photo = profilePhoto(user);
  const upload = async (x) => {
    try {
      setUser((await api.put("/me", { photo_path: x.path })).data);
      toast.success(t("saved"));
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return (
    <section className="flex flex-wrap items-center gap-6 rounded-3xl border border-slate-200 bg-white p-6 md:p-8" data-testid="profile-photo-card">
      <div className="flex h-28 w-28 shrink-0 items-center justify-center overflow-hidden rounded-full bg-gradient-to-br from-[#0A2540] to-[#00A8B5] font-display text-3xl font-bold text-white">
        {photo ? <img src={fileUrl(photo)} alt={user.first_name} className="h-full w-full object-cover" data-testid="profile-photo" /> : `${user.first_name?.[0] || ""}${user.last_name?.[0] || ""}`}
      </div>
      <div className="space-y-2">
        <p className="font-display text-2xl font-bold text-[#0A2540]" data-testid="profile-name">{user.first_name} {user.last_name}</p>
        <p className="text-sm text-slate-500">{user.email}</p>
        <FileUpload accept="image/*" label={photo ? t("change_photo") : t("profile_photo")} onUploaded={upload} testid="profile-photo-upload" />
      </div>
    </section>
  );
};

export function Profile() {
  const { user, setUser } = useAuth();
  const { t } = useLang();
  const [f, setF] = useState({ ...user });
  const admin = user.role === "admin";
  const save = async () => {
    try {
      const { data } = await api.put("/me", f);
      setUser(data);
      toast.success(t("saved"));
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return (
    <div className="space-y-6" data-testid="account-profile">
      <H sub={user.email}>{t("acc_profile")}</H>
      <PhotoCard />
      {!admin && <Sailors />}
      <Card title={t("account_holder")}>
        <SailorFields f={f} setF={setF} holder />
        <button onClick={save} className="btn-buoy mt-6" data-testid="profile-save-button">{t("save")}</button>
      </Card>
      {!admin && <AppPanel />}
    </div>
  );
}

const DOC_TYPES = ["Medical certificate", "Membership card", "Insurance", "Parental consent", "ID document", "Other"];

export function Documents() {
  const { user } = useAuth();
  const { t, tr, lang } = useLang();
  const { data, reload } = useFetch("/me/documents");
  const { data: regs } = useFetch("/me/registrations");
  const parts = user.participants || [];
  const [f, setF] = useState({ doc_type: DOC_TYPES[0], registration_id: "", participant_id: parts[0]?.id || "" });
  const add = async (x) => {
    try {
      await api.post("/me/documents", { ...f, registration_id: f.registration_id || null, participant_id: f.participant_id || null, path: x.path, filename: x.filename });
      toast.success(t("saved"));
      reload();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const pname = (id) => parts.find((p) => p.id === id)?.first_name || "";
  return (
    <div data-testid="account-documents">
      <H>{t("acc_docs")}</H>
      <div className="mb-8 flex flex-wrap items-center gap-3 rounded-3xl border border-slate-200 bg-white p-5">
        {parts.length > 1 && <select value={f.participant_id} onChange={(e) => setF({ ...f, participant_id: e.target.value })} className="h-10 rounded-xl border border-slate-200 px-3 text-sm" data-testid="doc-participant-select">{parts.map((p) => <option key={p.id} value={p.id}>{p.first_name} {p.last_name}</option>)}</select>}
        <select value={f.doc_type} onChange={(e) => setF({ ...f, doc_type: e.target.value })} className="h-10 rounded-xl border border-slate-200 px-3 text-sm" data-testid="doc-type-select">{DOC_TYPES.map((d) => <option key={d}>{d}</option>)}</select>
        <select value={f.registration_id} onChange={(e) => setF({ ...f, registration_id: e.target.value })} className="h-10 rounded-xl border border-slate-200 px-3 text-sm" data-testid="doc-registration-select">
          <option value="">—</option>{(regs || []).filter((r) => !f.participant_id || r.participant_id === f.participant_id).map((r) => <option key={r.id} value={r.id}>{tr(r.event, "title")}</option>)}
        </select>
        <FileUpload label={t("upload")} onUploaded={add} testid="doc-file-upload" />
      </div>
      {!data?.length && <p className="text-slate-500">{t("no_items")}</p>}
      <div className="grid gap-3 md:grid-cols-2">
        {(data || []).map((d) => (
          <a key={d.id} href={fileUrl(d.path)} target="_blank" rel="noreferrer" className="flex items-center gap-4 rounded-2xl border border-slate-200 bg-white p-4 hover:border-[#00A8B5]" data-testid={`document-${d.id}`}>
            <FileText className="text-[#00A8B5]" /><div className="min-w-0"><p className="font-semibold text-[#0A2540]">{d.doc_type} {pname(d.participant_id) && `· ${pname(d.participant_id)}`}</p><p className="truncate text-xs text-slate-500">{d.filename} · {fmtDate(d.created_at, lang)}</p></div>
          </a>
        ))}
      </div>
    </div>
  );
}
